import React, { useEffect } from "react";
import { useBCMSContext, BCMSRecord, useStateManager, BCMSStateManagerCRUD, useBCMSModal, useModel } from "bcms-react";
import Classes, { QuietTime, User, Timezone, ContactInfo, ScheduledTime } from "model";

import CustomSelect, { CustomSelectItem } from "components/custom-ui/CustomSelect";
import { CenterContentContainer, PageContainer, PageContentView } from "components/shared";
import UINotifiHeader from "components/shared/notifi/UINotifiHeader";
import DeliveryMethodGroup, { ContactRowNoDevice, NotificationBanner, ContactRow } from "./components/DeliveryMethodGroup";
import QuietTimesWidget from "./components/QuietTimesWidget";
import "./styles.css";
import "./mobile.css";

export default function NotificationsSettingsPage(props) {
    const globalController = useBCMSContext();
    BCMSRecord.globalControllerFor(Classes, globalController);

    const data = { ...getDefaultPageData(globalController), ...props.data ?? {} };
    const ui = { ...getDefaultPageUI(), ...props.ui ?? {} };

    const getByID = (array, id) => array.find(item => item.id === id);
    const insertIntoArray = (array, value) => array.map(item => item.id === value.id ? value : item, []);

    // checkSetTimeOfDayIsValid :: (STRING) -> BOOLEAN
    // Returns true if the quiet time toggle, start, end, and weekday values are valid.
    const checkSetTimeOfDayIsValid = quietTime => {
        const startScheduledTime = quietTime.startScheduledTime;
        const endScheduledTime = quietTime.endScheduledTime;
        const weekdayIDs = quietTime.weekdayIDs;

        if(!quietTime.isEnabled) return false;
        if(weekdayIDs.length === 0) return false;
        // temp: this gets around an init bug in the class.
        if(weekdayIDs.length === 1 && weekdayIDs[0] === "") return false;

        const startValid = time => {
            const { hour, minute } = time;
            if((hour === "" || isNaN(hour)) ||
            (minute === "" || isNaN(minute))) {
                return false
            } else {
                return true;
            }
        };
        const endValid = time => {
            const { hour, minute } = time;
            if((hour === "" || isNaN(hour)) ||
            (minute === "" || isNaN(minute))) {
                return false
            } else {
                return true;
            }
        };
        
        return startValid(startScheduledTime) && endValid(endScheduledTime);
    }

    
    // setPrimary :: (OBJECT, FUNCTION) -> OBJECT
    // Controller for the page.
    const controller = (state, setState) => ({
        data: state.data,
        ui: state.ui,

        // setPrimary :: (OBJECT, OBJECT) -> VOID
        // Sets a primary contact for the user.
        setPrimary: (controller, info) => {
            const contactUpdates = ContactInfo.initAll().map(item => {
                if(item.type === info.type) {
                  item.userUpdatedPrimaryStatus = item.id === info.id
                      ? true : false;
                  item.isPrimary = item.id === info.id
                      ? "true" : "false";
                }
                
                return ContactInfo.update(item);
            }, []);

            const user = controller.state.user;
            user[info.type] = info.id;

            BCMSRecord.batch([
              User.update(user),
              ...contactUpdates
            ], controller, Classes);

            BCMSStateManagerCRUD.init(state)
              .update([ "ui", "key" ], Date.now())
              .submit(setState);
        },

        // START: Functions to update a Quiet Time.
        // onSaveClick :: (STRING, STRING STRING) -> (BOOLEAN) -> VOID
        // Function for the "Save" button on click event.
        onSaveClick: (quietTimeID, type, uiKey) => value => {
            const selectedQuietTime = getByID(state.data.quietTimes, quietTimeID);
            const updates = [];

            if(selectedQuietTime.isEnabled) {
                updates.push(
                    ScheduledTime.update({ ...selectedQuietTime.startScheduledTime.record })
                );
                updates.push(
                    ScheduledTime.update({ ...selectedQuietTime.endScheduledTime.record })
                );
                updates.push(
                    QuietTime.update({ ...selectedQuietTime.record })
                );
            }

            const updatedQuietTime = QuietTime.init({ 
                ...selectedQuietTime.record,
                startScheduledTime: ScheduledTime.init({ ...selectedQuietTime.startScheduledTime.record }),
                endScheduledTime: ScheduledTime.init({ ...selectedQuietTime.endScheduledTime.record })
            });

            const updatedQuietTimesArr = insertIntoArray(state.data.quietTimes, updatedQuietTime);

            BCMSStateManagerCRUD.init(state)
              .update([ "data", "quietTimes" ], updatedQuietTimesArr)
              .update(["ui", uiKey, "action"], type === "save" ? "saved" : "updated")
              .submit(setState);
            
            BCMSRecord.batch([
              ...updates
            ], globalController, Classes);
        },

        // quietTimeIsEnabledGlobally :: (STRING) -> (BOOLEAN)
        // Returns true if the quiet time is globally enabled, not just in state.
        // Globally enabling requires a click on the "Save" button, not just a toggle "on".
        quietTimeIsEnabledGlobally: quietTimeID => {
            const globalQuietTime = QuietTime.initByID(quietTimeID);
            return globalQuietTime.isEnabled;
        },

        // canUpdateQuietTime :: (STRING) -> (BOOLEAN)
        // Returns true if the quiet time can be updated.
        canUpdateQuietTime: quietTimeID => {
            const globalQuietTime = QuietTime.initByID(quietTimeID);
            const globalStartScheduledTime = globalQuietTime.startScheduledTime;
            const globalEndScheduledTime = globalQuietTime.endScheduledTime;
            const stateQuietTime = getByID(state.data.quietTimes, quietTimeID);
            const stateStartScheduledTime = stateQuietTime.startScheduledTime;
            const stateEndScheduledTime = stateQuietTime.endScheduledTime;

            const objsAreEqual = (instance1, instance2, ignoreProps) => {
                const _instance1Record = { ...instance1.record };
                const _instance2Record = { ...instance2.record };
                return JSON.stringify(_instance1Record) === JSON.stringify(_instance2Record);
            }

            if(globalQuietTime === undefined) return false;
            if(!globalQuietTime.isEnabled) return false;
            if(!checkSetTimeOfDayIsValid(stateQuietTime)) return false;

            if(objsAreEqual(globalQuietTime, stateQuietTime) && 
            objsAreEqual(globalStartScheduledTime, stateStartScheduledTime) &&
            objsAreEqual(globalEndScheduledTime, stateEndScheduledTime)) {
                return false;
            } else {
                return true;
            }
        },

        // setTimeOfDayIsValid :: (STRING) -> (BOOLEAN)
        // Returns true if "checkSetTimeOfDayIsValid" returns true. 
        // This funtion is an interface for that function.
        setTimeOfDayIsValid: quietTimeID => {
            const selectedQuietTime = getByID(state.data.quietTimes, quietTimeID);
            return checkSetTimeOfDayIsValid(selectedQuietTime);
        },

        // updateScheduledTimeWeekdays :: (STRING) -> VOID
        // Updates the selected weekdays for a quiet time.
        updateScheduledTimeWeekdays: (quietTimeID) => (weekdayID, weekdayChecked) => {
            const selectedQuietTime = getByID(state.data.quietTimes, quietTimeID);
            let selectedWeekdayIDs = selectedQuietTime.weekdayIDs;

            if(weekdayChecked) {
                selectedWeekdayIDs = selectedWeekdayIDs.filter( id => id !== weekdayID);
            } else {
                selectedWeekdayIDs = [ ...selectedWeekdayIDs, weekdayID ].sort((a, b) => a - b);
            }

            const startScheduledTime = ScheduledTime.init({ ...selectedQuietTime.startScheduledTime.record });
            const endScheduledTime = ScheduledTime.init({ ...selectedQuietTime.endScheduledTime.record });
            const updatedQuietTime = QuietTime.init({ 
                ...selectedQuietTime.record, 
                weekdayIDs: [ ...selectedWeekdayIDs ]
            });

            updatedQuietTime.startScheduledTime = startScheduledTime;
            updatedQuietTime.endScheduledTime = endScheduledTime;
            const updatedQuietTimesArr = insertIntoArray(state.data.quietTimes, updatedQuietTime);

            BCMSStateManagerCRUD.init(state)
              .update([ "data", "quietTimes" ], updatedQuietTimesArr)
              .submit(setState);
        },

        // updateQuietTimeScheduledTime :: (STRING, STRING, STRING) -> (STRING) -> VOID
        // Updates a "start" or "end" scheduled time for a quiet time.
        updateQuietTimeScheduledTime: (id, prop, type) => value => {
            const selectedQuietTime = getByID(state.data.quietTimes, id);
            const selectedScheduledTime = selectedQuietTime[`${ type }ScheduledTime`];
            const otherScheduledTimeType = type === "start" ? "end" : "start";
            const otherScheduledTime = selectedQuietTime[`${ otherScheduledTimeType }ScheduledTime`];
            const updatedOtherScheduledTime = ScheduledTime.init({ ...otherScheduledTime.record });
            const updatedScheduledTime = ScheduledTime.init({ ...selectedScheduledTime.record });
            updatedScheduledTime[prop] = value;
            
            const updatedQuietTime = QuietTime.init({ ...selectedQuietTime.record });
            updatedQuietTime[`${ type }ScheduledTime`] = updatedScheduledTime;
            updatedQuietTime[`${ otherScheduledTimeType }ScheduledTime`] = updatedOtherScheduledTime;
            const updatedQuietTimesArr = insertIntoArray(state.data.quietTimes, updatedQuietTime);

            BCMSStateManagerCRUD.init(state)
              .update([ "data", "quietTimes" ], updatedQuietTimesArr)
              .submit(setState);
        },

        // updateQuietTime :: (STRING, STRING, STRING) -> (BOOLEAN) -> VOID
        // Updates the immediate properties of a quiet time (not the scheduled time props).
        updateQuietTime: (id, prop, uiKey) => value => {
            const selectedQuietTime = getByID(state.data.quietTimes, id);
            let startScheduledTime = ScheduledTime.init({ ...selectedQuietTime.startScheduledTime.record });
            let endScheduledTime = ScheduledTime.init({ ...selectedQuietTime.endScheduledTime.record });
            let updatedQuietTime = QuietTime.init({ 
                ...selectedQuietTime.record, 
                isEnabled: value
             });
            updatedQuietTime.startScheduledTime = startScheduledTime;
            updatedQuietTime.endScheduledTime = endScheduledTime;
            const updatedQuietTimesArr = insertIntoArray(state.data.quietTimes, updatedQuietTime);

            BCMSStateManagerCRUD.init(state)
              .update([ "data", "quietTimes" ], updatedQuietTimesArr)
              .submit(setState);

            // START: Delete an existing quiet time.
            const globalQuietTime = QuietTime.initByID(selectedQuietTime.id);

            if(globalQuietTime.isEnabled) {
                startScheduledTime = ScheduledTime.init({ 
                    ...selectedQuietTime.startScheduledTime.record, hour: "", minute: "", timeOfDay: "AM", type: ""
                 });
                endScheduledTime = ScheduledTime.init({ 
                    ...selectedQuietTime.endScheduledTime.record, hour: "", minute: "", timeOfDay: "AM", type: ""
                 });
                 updatedQuietTime.startScheduledTime = startScheduledTime;
                 updatedQuietTime.endScheduledTime = endScheduledTime;
                 updatedQuietTime.weekdayIDs = [""];

                const updatedQuietTimesArr = insertIntoArray(state.data.quietTimes, updatedQuietTime);

                BCMSStateManagerCRUD.init(state)
                .update([ "data", "quietTimes" ], updatedQuietTimesArr)
                .submit(setState);

                BCMSRecord.batch([
                    ScheduledTime.update({ ...startScheduledTime.record }),
                    ScheduledTime.update({ ...endScheduledTime.record }),
                    QuietTime.update({ ...updatedQuietTime.record })
                  ], globalController, Classes);

                BCMSStateManagerCRUD.init(state)
                .update(["ui", uiKey, "action"], "deleted")
                .submit(setState);
            }
            // END: Delete an existing quiet time.
        },
        // END: Functions to update a Quiet Time.

        // setTimezone :: (OBJECT, STRING) -> VOID
        // Sets the user's timezone.
        setTimezone: (controller, value) => {
            const user = controller.state.user;
            user.timezone = Timezone.getByValue(value);

            User.update(user)
                .submit(Classes);
            
            var didUpdateTimezone = ui.timezonePrompt.isActive;
            if(data.selectedTimezone !== value) {
                didUpdateTimezone = true;
            }

            BCMSStateManagerCRUD.init(state)
                .update([ "data", "selectedTimezone" ], value)
                .update([ "ui", "timezonePrompt", "isActive" ], didUpdateTimezone)
                .submit(setState);
        },

        // refresh :: (VOID) -> VOID
        // Rerenders the ui of the page.
        refresh: () => {
          BCMSStateManagerCRUD.init(state)
            .update([ "ui", "key" ], Date.now())
            .submit(setState);
        }
    });

    return (
        <PageContainer className="notifications-settings-page" state={ { data, ui } } controller={ controller }>
            <NotificationsSettingsContent />
        </PageContainer>
    )
}

export const getDefaultPageData = controller => {
    const model = controller.state;
    const user = User.init(model.user);
    const timezone = user.timezone.value;
    const quietTimes = QuietTime.initAll();
    
    return {
        quietTimes,
        selectedTimezone: timezone
    };
};

export const getDefaultPageUI = () => ({
    timezonePrompt: { isActive: false },
    key: Date.now(),

    modal: {
        id: undefined,
        data: {}
    }
});

function NotificationsSettingsContent(props) {
    const { data, ui, canUpdateQuietTime, onSaveClick, quietTimeIsEnabledGlobally, 
    setTimeOfDayIsValid, setTimezone, updateQuietTime, 
    updateQuietTimeScheduledTime, updateScheduledTimeWeekdays } = useStateManager();
    const controller = useBCMSContext();

    BCMSRecord.globalControllerFor(Classes, controller);
    const model = controller.state;

    const user = User.init(model.user);
    const info = ContactInfo.initAll();
    const unconfirmedInfo = ContactInfo.initAll().filter(contact => contact.isConfirmed === "false", []);

    const options = model.branding.options;
    const hasNotifi = options.notifi_version_abi22 === "Notifi" || options.notifi_version_abi22 === "undefined";
    const hasQuietTimes = options.quiet_times_abi22 === "true";

    const phoneInfo = ContactInfo.getContactInfoSortedByPrimary(info, "phone");
    const emailInfo = ContactInfo.getContactInfoSortedByPrimary(info, "email");
    const alertInfo = ContactInfo.getContactInfoSortedByPrimary(info, "alert");

    const phoneRows = phoneInfo.map(info => <ContactRow key={ `Phone_${ info.id }` } contactInfo={ info } />, []);
    const emailRows = emailInfo.map(info => <ContactRow key={ `Email_${ info.id }` } contactInfo={ info } />, []);
    const alertRows = alertInfo.map(info => <ContactRow key={ `Alert_${ info.id }` } contactInfo={ info } />, []);

    const timezoneItems = Timezone.initAll().map(zone => <CustomSelectItem key={ zone.id }>{ zone.value }</CustomSelectItem>, []);
    const timezone = user.timezone.value;

    const onTimezoneChange = value => setTimezone(controller, value);
    const { open } = useBCMSModal();

    useEffect(() => {
        if(ui.modal.id !== undefined) { open(ui.modal.id, ui.modal.data); }
    }, [ props ]);

    const quietTimeWidgets = data.quietTimes.reduce((acc, item) => {
        acc.push(<QuietTimesWidget key={ item.id } quietTime={ item } contactType={ item.typeFormatted } 
            canUpdateQuietTime={ canUpdateQuietTime } onSaveClick={ onSaveClick } 
            quietTimeIsEnabledGlobally={ quietTimeIsEnabledGlobally} setTimeOfDayIsValid={ setTimeOfDayIsValid }
            updateQuietTime={ updateQuietTime } updateQuietTimeScheduledTime={ updateQuietTimeScheduledTime }
            updateScheduledTimeWeekdays={ updateScheduledTimeWeekdays } />);
        return acc;
    }, []);

    return (
        <PageContentView>
            <div className="notifications-page-container">
                <CenterContentContainer>
                    <UINotifiHeader headerText="Notifications Settings" dataPoint="Notifications_Settings">
                        <CustomSelect dataPoint="TimeZone" value={ timezone } onChange={ onTimezoneChange }>
                            { timezoneItems }
                        </CustomSelect>

                        { ui.timezonePrompt.isActive && <p className="set-successfully">Timezone Updated Successfully.</p> }
                    </UINotifiHeader>

                    { unconfirmedInfo.length > 0 && <NotificationBanner /> }

                    {   hasNotifi &&
                        <DeliveryMethodGroup groupTitle="Send device notifications to" groupType="device" dataPointHeaderTitle="DeviceNotificationHeader" dataPointHeaderAddButton="Add_0">
                            { /** <ContactRowNoDevice /> */}
                            { alertRows }
                        </DeliveryMethodGroup>  }

                    {   hasNotifi &&
                        <DeliveryMethodGroup groupTitle="Send text notifications to" groupType="phone" dataPointHeaderTitle="PhoneNumberHeader" dataPointHeaderAddButton="Add_1">
                            { phoneRows }
                        </DeliveryMethodGroup>
                    }

                    <DeliveryMethodGroup groupTitle="Send email notifications to" groupType="email" dataPointHeaderTitle="EmailHeader" dataPointHeaderAddButton="Add_2">
                        { emailRows }
                    </DeliveryMethodGroup>

                    { hasQuietTimes && 
                    <div className="quiet-times-widget-container flex-container flex-horizontal align-top flex-wrap">
                        { quietTimeWidgets }
                    </div>
                    }
                </CenterContentContainer>
            </div>
        </PageContentView>
    );
}